import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../db/context';
import { requestPermission } from '../notifications/system';
import { supabase, useSyncState } from '../sync/useSync';
import { currentSubscription, disablePush, enablePush, pushAvailable, pushSupported } from './push';

/** "Reminders while Nexus is closed" for this browser (Web Push). */
export function PushSettings() {
  const { t } = useTranslation();
  const store = useStore();
  const signedIn = useSyncState((s) => !!s.session);
  const [on, setOn] = useState<boolean | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pushSupported()) return;
    void pushAvailable().then(async (ok) => {
      setAvailable(ok);
      if (ok) setOn(!!(await currentSubscription()));
    });
  }, []);

  if (!pushSupported())
    return <p className="text-xs text-fg-muted">{t('notifications.push.unsupported')}</p>;
  if (available === false)
    return <p className="text-xs text-fg-muted">{t('notifications.push.noServiceWorker')}</p>;
  if (!signedIn) return <p className="text-xs text-fg-muted">{t('notifications.push.signIn')}</p>;

  const toggle = async () => {
    setBusy(true);
    setError(null);
    try {
      if (on) {
        await disablePush(supabase());
        setOn(false);
      } else {
        if ((await requestPermission()) !== 'granted')
          throw new Error(t('notifications.settings.denied'));
        await enablePush(supabase(), store.deviceId);
        setOn(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1 rounded-md bg-bg-subtle p-2 text-xs">
      <div className="grid grid-cols-[1fr_auto] items-center gap-3">
        <span>{on ? t('notifications.push.on') : t('notifications.push.off')}</span>
        <button
          type="button"
          disabled={busy || on === null}
          onClick={() => void toggle()}
          className="rounded-lg border border-border px-2 py-1 hover:bg-bg-hover disabled:opacity-50"
        >
          {on ? t('notifications.push.disable') : t('notifications.push.enable')}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
